<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { uploadProps } from 'naive-ui';
import { IconAttach } from '@/components/design/icons';
import GaiaButton from './GaiaButton.vue';
import GaiaText from './GaiaText.vue';
import {
  gaiaControlHeight,
  gaiaDashedButtonArmedOverrides,
  gaiaDashedButtonOverrides,
} from '../themes/tokens';

const props = defineProps({
  ...uploadProps,
  /** trigger = the dashed attach button (composer), dropzone = the full drop area. */
  variant: {
    type: String as PropType<'trigger' | 'dropzone'>,
    default: 'trigger',
  },
  /**
   * A file drag is in flight over the page, so the trigger is a live drop
   * target and says so. The click and drop halves of the same control — hence
   * a state on the trigger rather than a second component.
   */
  dragging: {
    type: Boolean,
    default: false,
  },
});

// `variant` / `dragging` are ours, not n-upload's — keep them off the DOM element.
const passThrough = computed(() => {
  const { variant, dragging, ...rest } = props;
  return rest;
});

// The trigger shares the composer footer with the chat-settings pill, so its
// height comes from the shared control token rather than the Button theme's
// small step (which is naive's 28px and reads short beside the pill).
const dashedOverrides = computed(() => ({
  ...(props.dragging ? gaiaDashedButtonArmedOverrides : gaiaDashedButtonOverrides),
  heightSmall: gaiaControlHeight.composerControl,
}));
</script>

<template>
  <!-- naive sets `.n-upload { width: 100% }`, which is right for the dropzone and
       wrong for the trigger: as a flex child it then eats the whole row and shoves
       its siblings (the composer's chat-settings pill) to the far edge. There is
       no theme var for the root's width, so the trigger overrides it inline. -->
  <n-upload
    v-bind="passThrough"
    :style="props.variant === 'trigger' ? { width: 'auto' } : undefined"
  >
    <n-upload-dragger v-if="props.variant === 'dropzone'" class="gaia-file-upload__dropzone">
      <!-- Dropzone chrome comes from the Upload theme tokens (gaia-light.json). -->
      <gaia-text weight="semibold" tone="ink">
        <slot>Drop files to add them to Insights</slot>
      </gaia-text>
      <gaia-text tone="subtle" :style="{ display: 'block', marginTop: '6px' }">
        <slot name="description">We extract parties, dates and terms automatically</slot>
      </gaia-text>
    </n-upload-dragger>
    <gaia-button v-else size="small" :theme-overrides="dashedOverrides">
      <template #icon>
        <n-icon :component="IconAttach" :size="14" />
      </template>
      <slot v-if="props.dragging" name="dragging">Drop to attach</slot>
      <slot v-else>Attach or drop files</slot>
    </gaia-button>
  </n-upload>
</template>
